import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

/** Синхронно с --overlay-duration в tokens.css */
const OVERLAY_DURATION_MS = 380;

type OverlayOnClose = () => void | Promise<void>;

/**
 * Два requestAnimationFrame: первый кадр — композит «from», второй — старт @keyframes,
 * иначе анимация открытия часто схлопывается в 2–4 кадра (один коммит с DOM + анимацией).
 */
function scheduleEnterReady(setEnterReady: (v: boolean) => void) {
  let id1 = 0;
  let id2 = 0;
  id1 = requestAnimationFrame(() => {
    id2 = requestAnimationFrame(() => setEnterReady(true));
  });
  return () => {
    cancelAnimationFrame(id1);
    cancelAnimationFrame(id2);
  };
}

/**
 * Хук для единой анимации появления/исчезновения overlay (модалки, поповеры, меню).
 * При закрытии не вызывает onClose сразу — ждёт завершения анимации.
 * useLayoutEffect для синхронизации isExiting до paint — устраняет моргание.
 * onClose может быть async: exitComplete ставится только после него — иначе при ещё открытом
 * попапе и exitComplete===true сбрасывался isExiting и анимация закрытия шла дважды.
 * @param positioned — true для поповеров/меню (лёгкий translateY + opacity, без крупного scale у модалок)
 */
export function useOverlayAnimation(open: boolean, onClose: OverlayOnClose, positioned = false) {
  const [isExiting, setIsExiting] = useState(false);
  const [exitComplete, setExitComplete] = useState(false);
  const [enterReady, setEnterReady] = useState(false);
  const onCloseRef = useRef(onClose);
  const hasBeenOpenRef = useRef(false);
  const prevOpenRef = useRef(false);
  onCloseRef.current = onClose;

  if (open) {
    hasBeenOpenRef.current = true;
  }

  useLayoutEffect(() => {
    if (!open) {
      setEnterReady(false);
      return;
    }
    setEnterReady(false);
    return scheduleEnterReady(setEnterReady);
  }, [open]);

  useLayoutEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open) {
      if (!wasOpen) {
        setIsExiting(false);
        setExitComplete(false);
      }
    } else if (hasBeenOpenRef.current && !exitComplete) {
      setIsExiting(true);
    }
  }, [open, exitComplete]);

  useEffect(() => {
    if (!isExiting) return;
    let cancelled = false;
    const t = setTimeout(() => {
      void Promise.resolve(onCloseRef.current())
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setExitComplete(true);
        });
    }, OVERLAY_DURATION_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isExiting]);

  const requestClose = useCallback(() => {
    setIsExiting(true);
  }, []);

  const shouldRender = open || (hasBeenOpenRef.current && !exitComplete);

  const backdropClasses = isExiting
    ? 'overlay-backdrop-exit'
    : enterReady
      ? 'overlay-backdrop-enter'
      : 'overlay-backdrop-enter-prep';

  const contentClasses = isExiting
    ? positioned
      ? 'overlay-content-exit-positioned'
      : 'overlay-content-exit'
    : enterReady
      ? positioned
        ? 'overlay-content-enter-positioned'
        : 'overlay-content-enter'
      : positioned
        ? 'overlay-content-enter-positioned-prep'
        : 'overlay-content-enter-prep';

  return {
    shouldRender,
    backdropClasses,
    contentClasses,
    requestClose,
    isExiting,
  };
}
